import { api, imgSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Loader2, X } from "lucide-react";

const GenerationCard = ({ gen, session, exportingId, onExport }) => (
  <Card data-testid="studio-generation-card" className="overflow-hidden rounded-2xl">
    <img src={imgSrc({ path: gen.image_path })} alt={gen.garment_name} className="aspect-[3/4] w-full object-cover" />
    <CardContent className="space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-tight">{gen.garment_name}</p>
        {gen.exported ? (
          <Badge className="shrink-0 rounded-full bg-[hsl(174,38%,34%)] text-[10px] hover:bg-[hsl(174,38%,34%)]">Exported</Badge>
        ) : (
          <Badge variant="secondary" className="shrink-0 rounded-full text-[10px]">Preview</Badge>
        )}
      </div>
      <p className="line-clamp-1 text-[11px] text-muted-foreground">{gen.scene_used}</p>
      {gen.exported ? (
        <Button size="sm" variant="outline" data-testid="studio-download-button"
          onClick={() => window.open(`${api.defaults.baseURL}/images/${gen.image_path}`, "_blank")}
          className="w-full rounded-xl border bg-card text-xs hover:bg-muted">
          <Download className="mr-1.5 h-3.5 w-3.5" /> Download watermarked file
        </Button>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" data-testid="studio-export-button" disabled={exportingId === gen.id} className="w-full rounded-xl text-xs">
              {exportingId === gen.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
              Approve & export final
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-xl">Approve & export this image?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll receive the flat, digitally watermarked marketing image. Exporting closes this
                temporary twin session — {session.model_name}'s underlying AI profile is never downloadable.
                You can open a new session anytime while the licence is active.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl"><X className="mr-1 h-3.5 w-3.5" /> Not yet</AlertDialogCancel>
              <AlertDialogAction data-testid="studio-export-confirm-button" className="rounded-xl" onClick={() => onExport(gen)}>
                Export & close session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </CardContent>
  </Card>
);

export const GenerationGallery = ({ generations, generating, selectedGarment, session, exportingId, onExport }) => (
  <div>
    <h2 className="font-display text-xl">Generated imagery</h2>
    <p className="text-xs text-muted-foreground">All previews are digitally watermarked with your licence registry ID.</p>
    {generations.length === 0 && !generating ? (
      <div className="mt-4 rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
        Nothing generated yet — pick a garment, choose a scene, and hit Generate.
      </div>
    ) : (
      <div data-testid="studio-gallery" className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {generating && (
          <div className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(174,38%,34%)]" />
            <p className="px-4 text-center text-xs">Draping '{selectedGarment?.name}' onto {session.model_name}'s twin…</p>
          </div>
        )}
        {generations.map((g) => (
          <GenerationCard key={g.id} gen={g} session={session} exportingId={exportingId} onExport={onExport} />
        ))}
      </div>
    )}
  </div>
);
